import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import moment from 'moment';
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Tag,
} from 'antd';
// API
import {
  GET_CB_ACCOUNT_BALANCE,
  GET_CB_BALANCE_TRANSACTIONS,
} from 'api/cbBalance/queries';
import {
  ADMIN_ADJUST_CB_BALANCE,
  ADMIN_SET_CB_BALANCE_NEGATIVE_ALLOWED,
} from 'api/cbBalance/mutations';
// Types
import { SortDirection } from 'api/graphql-global-types';
import {
  GetCbAccountBalance,
  GetCbAccountBalanceVariables,
} from 'api/cbBalance/types/GetCbAccountBalance';
import {
  GetCbBalanceTransactions,
  GetCbBalanceTransactionsVariables,
  GetCbBalanceTransactions_getCbBalanceTransactions_entities,
} from 'api/cbBalance/types/GetCbBalanceTransactions';
import {
  AdminAdjustCbBalance,
  AdminAdjustCbBalanceVariables,
} from 'api/cbBalance/types/AdminAdjustCbBalance';
import {
  AdminSetCbBalanceNegativeAllowed,
  AdminSetCbBalanceNegativeAllowedVariables,
} from 'api/cbBalance/types/AdminSetCbBalanceNegativeAllowed';
// UI
import Table from 'ui/Table';
import StatisticColumns from 'ui/StatisticColumns/StatisticColumns';
import { errorNotification, successNotification } from 'ui/Notification';
// Helpers
import { formatCurrencyString } from 'helpers/utils';

type Transaction = GetCbBalanceTransactions_getCbBalanceTransactions_entities;

export type CbAccountBalancePanelProps = {
  ownerId: string;
};

const CbAccountBalancePanel: React.FC<CbAccountBalancePanelProps> = ({
  ownerId,
}) => {
  const [adjustVisible, setAdjustVisible] = useState<boolean>(false);
  const [negativeVisible, setNegativeVisible] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const [adjustForm] = Form.useForm();
  const [negativeForm] = Form.useForm();

  const { data, loading } = useQuery<
    GetCbAccountBalance,
    GetCbAccountBalanceVariables
  >(GET_CB_ACCOUNT_BALANCE, {
    fetchPolicy: 'cache-and-network',
    variables: { input: { ownerId } },
  });

  const { data: txData, loading: txLoading } = useQuery<
    GetCbBalanceTransactions,
    GetCbBalanceTransactionsVariables
  >(GET_CB_BALANCE_TRANSACTIONS, {
    fetchPolicy: 'cache-and-network',
    variables: {
      input: {
        ownerId,
        direction: SortDirection.DESC,
        limit: pageSize,
        offset: (currentPage > 1 ? currentPage - 1 : 0) * pageSize,
      },
    },
  });

  const balance = data?.getCbAccountBalance;
  const transactions = txData?.getCbBalanceTransactions;

  // Adjust returns the updated CbAccountBalance (normalised by id, so the
  // balance card updates itself); we only need to refetch the ledger list.
  const [adminAdjustCbBalance, { loading: adjusting }] = useMutation<
    AdminAdjustCbBalance,
    AdminAdjustCbBalanceVariables
  >(ADMIN_ADJUST_CB_BALANCE, {
    refetchQueries: ['GetCbBalanceTransactions'],
  });

  // Negative-allowed returns Boolean, so refetch both the balance and ledger.
  const [adminSetNegativeAllowed, { loading: settingNegative }] = useMutation<
    AdminSetCbBalanceNegativeAllowed,
    AdminSetCbBalanceNegativeAllowedVariables
  >(ADMIN_SET_CB_BALANCE_NEGATIVE_ALLOWED, {
    refetchQueries: ['GetCbAccountBalance', 'GetCbBalanceTransactions'],
  });

  const handleAdjust = async (): Promise<void> => {
    const values = await adjustForm.validateFields();

    if (!values.paidDelta && !values.promoDelta) {
      errorNotification('Enter a paid and/or promo delta.');

      return;
    }

    try {
      await adminAdjustCbBalance({
        variables: {
          input: {
            ownerId,
            reason: values.reason,
            paidDelta: values.paidDelta || undefined,
            promoDelta: values.promoDelta || undefined,
          },
        },
      });
      successNotification('Balance adjusted.');
      setAdjustVisible(false);
      adjustForm.resetFields();
    } catch (error) {
      errorNotification((error as Error)?.message);
    }
  };

  const handleSetNegative = async (): Promise<void> => {
    const values = await negativeForm.validateFields();

    try {
      await adminSetNegativeAllowed({
        variables: {
          input: {
            ownerId,
            allowed: !balance?.negativeBalanceAllowed,
            reason: values.reason,
          },
        },
      });
      successNotification('Overdraft setting updated.');
      setNegativeVisible(false);
      negativeForm.resetFields();
    } catch (error) {
      errorNotification((error as Error)?.message);
    }
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (value: string) =>
        value ? moment(value).format('MMM DD YYYY, HH:mm') : '-',
    },
    { title: 'Type', dataIndex: 'type', key: 'type' },
    {
      title: 'Paid Δ',
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      render: (value: number) => formatCurrencyString(value),
    },
    {
      title: 'Promo Δ',
      dataIndex: 'promoAmount',
      key: 'promoAmount',
      render: (value: number) => formatCurrencyString(value),
    },
    {
      title: 'Paid after',
      dataIndex: 'paidBalanceAfter',
      key: 'paidBalanceAfter',
      render: (value: number) => formatCurrencyString(value),
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      render: (_: string, record: Transaction) =>
        record.reason || record.campaignId || '-',
    },
    {
      title: 'By',
      dataIndex: 'performedByName',
      key: 'performedByName',
      render: (_: string, record: Transaction) =>
        record.performedByName || record.performedByEmail || 'System',
    },
  ];

  return (
    <Card
      title="Account Balance"
      loading={loading && !balance}
      extra={
        <Space>
          <Button size="small" onClick={() => setAdjustVisible(true)}>
            Adjust
          </Button>
          <Button size="small" onClick={() => setNegativeVisible(true)}>
            {balance?.negativeBalanceAllowed
              ? 'Disable overdraft'
              : 'Allow overdraft'}
          </Button>
        </Space>
      }
    >
      <StatisticColumns
        style={{ marginBottom: 16 }}
        items={[
          {
            title: 'Paid',
            value: formatCurrencyString(balance?.paidBalance ?? 0),
          },
          {
            title: 'Promo',
            value: formatCurrencyString(balance?.promoBalance ?? 0),
          },
          {
            title: 'Total',
            value: formatCurrencyString(balance?.totalBalance ?? 0),
          },
          {
            title: 'Refundable',
            value: formatCurrencyString(balance?.availableToRefund ?? 0),
          },
        ]}
      />

      <div style={{ marginBottom: 16 }}>
        Overdraft:{' '}
        {balance?.negativeBalanceAllowed ? (
          <Tag color="orange">Allowed</Tag>
        ) : (
          <Tag color="default">Not allowed</Tag>
        )}
      </div>

      <Table<Transaction, string>
        columns={columns}
        data={transactions?.entities}
        loading={txLoading}
        total={transactions?.total}
        currentPage={currentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        setCurrentPage={setCurrentPage}
        scroll={{ x: 800 }}
      />

      <Modal
        title="Adjust account balance"
        visible={adjustVisible}
        okText="Adjust"
        onOk={handleAdjust}
        onCancel={() => setAdjustVisible(false)}
        confirmLoading={adjusting}
        destroyOnClose
      >
        <Form form={adjustForm} layout="vertical">
          <Form.Item
            name="paidDelta"
            label="Paid delta ($)"
            tooltip="Signed. Negative below zero requires overdraft to be allowed."
          >
            <InputNumber step={0.01} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="promoDelta"
            label="Promo delta ($)"
            tooltip="Signed. Promo can never go below zero."
          >
            <InputNumber step={0.01} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'A reason is required' }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={
          balance?.negativeBalanceAllowed
            ? 'Disable overdraft'
            : 'Allow overdraft'
        }
        visible={negativeVisible}
        okText="Confirm"
        onOk={handleSetNegative}
        onCancel={() => setNegativeVisible(false)}
        confirmLoading={settingNegative}
        destroyOnClose
      >
        <p>
          This is a brand-level setting for the owner&apos;s wallet (the
          team&apos;s if the brand has one, otherwise the personal one).
        </p>
        <Form form={negativeForm} layout="vertical">
          <Form.Item
            name="reason"
            label="Reason"
            rules={[{ required: true, message: 'A reason is required' }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default CbAccountBalancePanel;
